/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
'use client';

import { Suspense, lazy, useState, type ReactElement } from 'react';
import { Truck } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/buttons';
import { cn } from '@/lib/utils';
import { useDeliveryZip } from '@/providers/store-and-delivery-zip-provider';

const DeliveryZipSheet = lazy(() => import('@/components/delivery-zip-flyout/delivery-zip-sheet'));

export interface ShipToBadgeProps {
    /** Override for the provider-supplied ZIP. Primarily for stories/tests. */
    postalCode?: string;
    className?: string;
    /**
     * Override the default behaviour (open the delivery-ZIP sheet). Provided so
     * stories and tests can assert click handling without mounting the sheet.
     */
    onClick?: () => void;
}

export default function ShipToBadge({ postalCode, className, onClick }: ShipToBadgeProps): ReactElement {
    const { t } = useTranslation('header');
    const { zip: providerZip } = useDeliveryZip();
    const zip = postalCode ?? providerZip;
    const [open, setOpen] = useState<boolean>(false);
    const [interacted, setInteracted] = useState<boolean>(false);

    const label = zip ? t('shipTo.label', { postalCode: zip }) : t('shipTo.label', { postalCode: 'XXXXX' });
    const ariaLabel = zip ? t('shipTo.openAriaLabel', { postalCode: zip }) : t('shipTo.setAriaLabel');

    const handleClick = () => {
        if (onClick) {
            onClick();
            return;
        }
        setInteracted(true);
        setOpen(true);
    };

    const trigger = (
        <Button
            variant="ghost"
            className={cn(
                'cursor-pointer gap-1 px-1 normal-case tracking-normal hover:bg-transparent hover:opacity-50 lg:px-4',
                className
            )}
            onClick={handleClick}
            aria-label={ariaLabel}
            aria-haspopup="dialog"
            aria-expanded={open ? true : undefined}
            data-testid="ship-to-badge">
            <Truck className="size-6" aria-hidden="true" />
            <span className="hidden text-xs font-semibold leading-4 lg:inline">{label}</span>
        </Button>
    );

    if (onClick || !interacted) {
        return trigger;
    }

    return (
        <Suspense fallback={trigger}>
            <DeliveryZipSheet open={open} onOpenChange={setOpen} initialZip={zip}>
                {trigger}
            </DeliveryZipSheet>
        </Suspense>
    );
}
